import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import * as PIXI from "pixi.js";
import { Howler } from "howler";
import { useSelector } from "react-redux";
import { soundManager } from "../SoundManager";
import { selectPotCollectionActive } from "../State/selectors";

export const AppActivityContext = createContext(null);

// ── Spin idle timer ──
// After SPIN_IDLE_INTERVAL ms of no spin activity, trigger ambient music crossfade
const SPIN_IDLE_INTERVAL = 30000; // 30 seconds

/**
 * AppActivityProvider — Production-grade tab visibility management
 *
 * Provides `isActive` state that tracks whether the browser tab is visible.
 * When the tab is hidden:
 *   - Audio is muted via Howler
 *   - Pixi ticker speed is set to 0 (freezes rendering without killing the loop)
 *
 * Also tracks spin idle state:
 *   - `registerSpin()` — call when a spin starts to reset the idle timer
 *   - `spinIdleTick` — counter that increments every 30s of no spin activity
 *
 * Design decisions:
 *   1. Uses ONLY `visibilitychange` — `blur`/`focus` cause false positives
 *      (DevTools, alerts, iframes). `pagehide`/`freeze`/`pageshow` are redundant
 *      as they always fire alongside or after `visibilitychange`.
 *   2. Uses `ticker.speed = 0` on BOTH app.ticker and Ticker.shared instead of
 *      `ticker.stop()` — stop() kills the loop and spikes delta on restart.
 *      speed=0 keeps the loop alive with deltaMS=0 so spins, TickerTimeouts,
 *      and spine all freeze cleanly and resume without wall-clock drift.
 *   3. 100ms debounce prevents rapid state flicker from quick tab switching
 *      or edge cases where visibility events fire multiple times.
 */
export const AppActivityProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(true);
  const [spinIdleTick, setSpinIdleTick] = useState(0);
  const spinIdleTimerRef = useRef(null);

  // Start (or restart) the repeating idle timer
  const startIdleTimer = useCallback(() => {
    if (spinIdleTimerRef.current) clearInterval(spinIdleTimerRef.current);
    spinIdleTimerRef.current = setInterval(() => {
      setSpinIdleTick((prev) => {
        const next = prev + 1;
        // Crossfade to ambient on first tick (30s of inactivity)
        if (next === 1) {
          soundManager.crossfadeToAmbient();
        }
        return next;
      });
    }, SPIN_IDLE_INTERVAL);
  }, []);

  // Called by consumers when a spin happens — resets the idle cycle
  const registerSpin = useCallback(() => {
    setSpinIdleTick(0);
    startIdleTimer();
    // Crossfade back from ambient to background music
    soundManager.crossfadeToBackground();
  }, [startIdleTimer]);

  // Start initial idle timer on mount + global interaction listener
  useEffect(() => {
    startIdleTimer();

    // Any user interaction (click/tap/keypress) resets idle and restores background music
    const handleInteraction = () => {
      setSpinIdleTick((prev) => {
        if (prev > 0) {
          soundManager.crossfadeToBackground();
        }
        return 0;
      });
      startIdleTimer();
    };

    document.addEventListener('pointerdown', handleInteraction);
    document.addEventListener('keydown', handleInteraction);

    return () => {
      if (spinIdleTimerRef.current) clearInterval(spinIdleTimerRef.current);
      document.removeEventListener('pointerdown', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [startIdleTimer]);

  // Pause idle timer during bonus/respin game — the game is actively playing
  const potCollectionActive = useSelector(selectPotCollectionActive);
  useEffect(() => {
    if (potCollectionActive) {
      // Feature active — clear idle timer so ambient crossfade doesn't trigger
      if (spinIdleTimerRef.current) {
        clearInterval(spinIdleTimerRef.current);
        spinIdleTimerRef.current = null;
      }
      // If already crossfaded to ambient, switch back
      setSpinIdleTick(0);
      soundManager.crossfadeToBackground();
    } else {
      // Feature ended — restart idle timer fresh
      startIdleTimer();
    }
  }, [potCollectionActive, startIdleTimer]);

  useEffect(() => {
    let debounceTimer = null;

    const handleVisibilityChange = () => {
      const active = document.visibilityState === "visible";

      // Debounce: prevent rapid state flicker from quick tab switching
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(() => {
        setIsActive((prev) => {
          if (prev === active) return prev;

          // Mute/unmute audio
          Howler.mute(!active);

          // Freeze/unfreeze Pixi tickers without killing the loop.
          // speed = 0 → deltaMS = 0 for all subscribers (spins, TickerTimeouts,
          // spine). Must set BOTH app.ticker and Ticker.shared — game logic
          // and TickerTimeout use shared; the stage renders on app.ticker.
          const speed = active ? 1 : 0;
          const app = globalThis.__PIXI_APP__;
          if (app && app.ticker) {
            app.ticker.speed = speed;
          }
          PIXI.Ticker.shared.speed = speed;

          return active;
        });
      }, 100);
    };

    // Initialize with current state
    handleVisibilityChange();

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, []);

  // Stable context value — only re-creates when isActive or spinIdleTick changes
  const value = useMemo(
    () => ({
      isActive,
      spinIdleTick,
      registerSpin,
    }),
    [isActive, spinIdleTick, registerSpin]
  );

  return (
    <AppActivityContext.Provider value={value}>
      {children}
    </AppActivityContext.Provider>
  );
};

// Hook for consuming the context
export const useAppActivity = () => {
  const context = useContext(AppActivityContext);
  if (!context) {
    throw new Error(
      "useAppActivity must be used within an AppActivityProvider"
    );
  }
  return context;
};
